import { useEffect, useState } from "react";

/**
 * Returns true once `timeoutMs` has elapsed since `lastActivityMs`.
 *
 * Uses a single timer scheduled for the exact expiry moment (no polling), re-armed
 * only when the activity time or timeout changes. A `visibilitychange` re-check
 * covers background tabs where the timer may have been throttled or suspended.
 *
 * `lastActivityMs` of null (e.g. an empty conversation) is never "ended".
 */
export const useSessionEnded = (lastActivityMs: number | null, timeoutMs: number): boolean => {
	const [ended, setEnded] = useState(() => lastActivityMs != null && Date.now() - lastActivityMs >= timeoutMs);

	useEffect(() => {
		if (lastActivityMs == null) {
			setEnded(false);
			return;
		}
		const remaining = lastActivityMs + timeoutMs - Date.now();
		if (remaining <= 0) {
			setEnded(true);
			return;
		}
		setEnded(false);

		const timer = setTimeout(() => setEnded(true), remaining);
		const onVisible = () => {
			if (document.visibilityState === "visible" && Date.now() - lastActivityMs >= timeoutMs) setEnded(true);
		};
		document.addEventListener("visibilitychange", onVisible);

		return () => {
			clearTimeout(timer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [lastActivityMs, timeoutMs]);

	return ended;
};
